import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { RoomTab } from '../components/multiroom/RoomTabs';
import type { RoomNotification } from '../components/multiroom/RoomNotifications';
import multiRoomWebSocket from '../services/multiRoomWebSocket';
import apiClient from '../services/apiClient';

// Multi-room state interfaces
export interface ActiveRoom {
  roomId: string;
  gameType: string;
  playerCount: number;
  maxPlayers: number;
  status: 'waiting' | 'playing' | 'your-turn' | 'finished';
  joinedAt: Date;
  isActive: boolean;
  gameState?: any; // Current game state for this room
  connection?: any; // WebSocket connection
  isConnected: boolean; // WebSocket connection status
  lastActivity?: Date; // Last activity timestamp
}

export interface MultiRoomState {
  // User capacity
  maxRooms: number;
  canJoinNewRoom: boolean;
  
  // Active rooms
  activeRooms: Map<string, ActiveRoom>;
  activeRoomId: string | null;
  
  // Notifications
  notifications: RoomNotification[];
  unreadCount: number;
  
  // UI state
  isLoading: boolean;
  error: string | null;
}

// Action types
type MultiRoomAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_MAX_ROOMS'; payload: number }
  | { type: 'ADD_ROOM'; payload: ActiveRoom }
  | { type: 'REMOVE_ROOM'; payload: string }
  | { type: 'UPDATE_ROOM'; payload: { roomId: string; updates: Partial<ActiveRoom> } }
  | { type: 'SET_ACTIVE_ROOM'; payload: string }
  | { type: 'ADD_NOTIFICATION'; payload: RoomNotification }
  | { type: 'MARK_NOTIFICATION_READ'; payload: string }
  | { type: 'MARK_ALL_NOTIFICATIONS_READ' }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'UPDATE_ROOM_CAPACITY'; payload: { current: number; max: number } };

// Initial state
const initialState: MultiRoomState = {
  maxRooms: 5,
  canJoinNewRoom: true,
  activeRooms: new Map(),
  activeRoomId: null,
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
};

// Reducer
function multiRoomReducer(state: MultiRoomState, action: MultiRoomAction): MultiRoomState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_MAX_ROOMS':
      return { 
        ...state, 
        maxRooms: action.payload,
        canJoinNewRoom: state.activeRooms.size < action.payload
      };

    case 'ADD_ROOM':
      const newRooms = new Map(state.activeRooms);
      newRooms.set(action.payload.roomId, action.payload);
      return {
        ...state,
        activeRooms: newRooms,
        activeRoomId: action.payload.isActive ? action.payload.roomId : state.activeRoomId,
        canJoinNewRoom: newRooms.size < state.maxRooms
      };

    case 'REMOVE_ROOM':
      const updatedRooms = new Map(state.activeRooms);
      updatedRooms.delete(action.payload);
      
      // If we're removing the active room, switch to another one
      let newActiveRoomId = state.activeRoomId;
      if (state.activeRoomId === action.payload) {
        const remainingRooms = Array.from(updatedRooms.keys());
        newActiveRoomId = remainingRooms.length > 0 ? remainingRooms[0] : null;
      }

      return {
        ...state,
        activeRooms: updatedRooms,
        activeRoomId: newActiveRoomId,
        canJoinNewRoom: updatedRooms.size < state.maxRooms
      };

    case 'UPDATE_ROOM':
      const roomsToUpdate = new Map(state.activeRooms);
      const existingRoom = roomsToUpdate.get(action.payload.roomId);
      if (existingRoom) {
        roomsToUpdate.set(action.payload.roomId, { ...existingRoom, ...action.payload.updates });
      }
      return { ...state, activeRooms: roomsToUpdate };

    case 'SET_ACTIVE_ROOM':
      // Update all rooms to set isActive correctly
      const roomsWithActiveUpdate = new Map(state.activeRooms);
      roomsWithActiveUpdate.forEach((room, roomId) => {
        room.isActive = roomId === action.payload;
      });
      return {
        ...state,
        activeRooms: roomsWithActiveUpdate,
        activeRoomId: action.payload
      };

    case 'ADD_NOTIFICATION':
      const newNotifications = [...state.notifications, action.payload];
      return {
        ...state,
        notifications: newNotifications,
        unreadCount: newNotifications.filter(n => !n.isRead).length
      };

    case 'MARK_NOTIFICATION_READ':
      const updatedNotifications = state.notifications.map(n =>
        n.id === action.payload ? { ...n, isRead: true } : n
      );
      return {
        ...state,
        notifications: updatedNotifications,
        unreadCount: updatedNotifications.filter(n => !n.isRead).length
      };

    case 'MARK_ALL_NOTIFICATIONS_READ':
      const allReadNotifications = state.notifications.map(n => ({ ...n, isRead: true }));
      return {
        ...state,
        notifications: allReadNotifications,
        unreadCount: 0
      };

    case 'REMOVE_NOTIFICATION':
      const filteredNotifications = state.notifications.filter(n => n.id !== action.payload);
      return {
        ...state,
        notifications: filteredNotifications,
        unreadCount: filteredNotifications.filter(n => !n.isRead).length
      };

    case 'UPDATE_ROOM_CAPACITY':
      return {
        ...state,
        canJoinNewRoom: action.payload.current < action.payload.max
      };

    default:
      return state;
  }
}

// Context type
interface MultiRoomContextType {
  state: MultiRoomState;
  
  // Room management
  joinRoom: (roomId: string, gameType: string) => Promise<void>;
  leaveRoom: (roomId: string) => Promise<void>;
  switchRoom: (roomId: string) => Promise<void>;
  updateRoomStatus: (roomId: string, status: ActiveRoom['status']) => void;
  
  // Notification management
  addNotification: (notification: Omit<RoomNotification, 'id' | 'timestamp'>) => void;
  markNotificationRead: (notificationId: string) => void;
  markAllNotificationsRead: () => void;
  dismissNotification: (notificationId: string) => void;
  
  // Utility functions
  getRoomTabs: () => RoomTab[];
  getActiveRoom: () => ActiveRoom | null;
  isInRoom: (roomId: string) => boolean;
  
  // API calls
  fetchUserRooms: () => Promise<void>;
  fetchRoomCapacity: () => Promise<void>;
}

// Create context
const MultiRoomContext = createContext<MultiRoomContextType | null>(null);

// Provider component
interface MultiRoomProviderProps {
  children: ReactNode;
}

export const MultiRoomProvider: React.FC<MultiRoomProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(multiRoomReducer, initialState);

  // Room management functions
  const joinRoom = useCallback(async (roomId: string, gameType: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      // Check capacity first - using apiClient (handles 401 auto-refresh)
      const capacityResponse = await apiClient.get('/user/rooms/capacity');
      const capacity = capacityResponse.data;
      
      if (!capacity.data.canJoinNewRoom) {
        throw new Error(`Cannot join room. Maximum rooms limit reached (${capacity.data.maxActiveRooms})`);
      }

      // Join the room - using apiClient
      await apiClient.post(`/matchmaking/rooms/${roomId}/join`);

      // Add room to state
      const newRoom: ActiveRoom = {
        roomId,
        gameType,
        playerCount: 1, // Will be updated from game state
        maxPlayers: gameType === 'kadi-ni-tidi' ? 4 : 4,
        status: 'waiting',
        joinedAt: new Date(),
        isActive: true, // New room becomes active
        isConnected: false, // Will be updated when WebSocket connects
        lastActivity: new Date()
      };

      dispatch({ type: 'ADD_ROOM', payload: newRoom });
      dispatch({ type: 'SET_ACTIVE_ROOM', payload: roomId });

      // Connect via WebSocket
      const username = 'Player'; // TODO: Get from auth context
      const wsConnected = await multiRoomWebSocket.connectToRoom(roomId, username, gameType);
      
      if (!wsConnected) {
        console.warn('Failed to establish WebSocket connection, but room joined successfully');
      }

      // Add success notification
      addNotification({
        roomId,
        type: 'player-joined',
        title: 'Joined Room',
        message: `Successfully joined room ${roomId.slice(-4)}`,
        isRead: false,
        priority: 'medium',
      });

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to join room' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const leaveRoom = useCallback(async (roomId: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Disconnect WebSocket first
      await multiRoomWebSocket.disconnectFromRoom(roomId);
      
      // Leave room via API - using apiClient
      await apiClient.delete(`/user/rooms/${roomId}`);

      // Remove from state
      dispatch({ type: 'REMOVE_ROOM', payload: roomId });

      // Add notification
      addNotification({
        roomId,
        type: 'player-left',
        title: 'Left Room',
        message: `Left room ${roomId.slice(-4)}`,
        isRead: false,
        priority: 'low',
      });

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to leave room' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const switchRoom = useCallback(async (roomId: string) => {
    try {
      // Switch active room via API - using apiClient
      await apiClient.post(`/user/rooms/${roomId}/activate`);

      // Update state
      dispatch({ type: 'SET_ACTIVE_ROOM', payload: roomId });

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to switch room' });
    }
  }, []);

  const updateRoomStatus = useCallback((roomId: string, status: ActiveRoom['status']) => {
    dispatch({ type: 'UPDATE_ROOM', payload: { roomId, updates: { status } } });
    
    // Add notification for status changes that require attention
    if (status === 'your-turn') {
      addNotification({
        roomId,
        type: 'your-turn',
        title: 'Your Turn',
        message: `It's your turn in room ${roomId.slice(-4)}`,
        isRead: false,
        actionRequired: true,
        priority: 'high',
      });
    }
  }, []);

  // Notification management
  const addNotification = useCallback((notification: Omit<RoomNotification, 'id' | 'timestamp'>) => {
    const fullNotification: RoomNotification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
    dispatch({ type: 'ADD_NOTIFICATION', payload: fullNotification });
  }, []);

  const markNotificationRead = useCallback((notificationId: string) => {
    dispatch({ type: 'MARK_NOTIFICATION_READ', payload: notificationId });
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    dispatch({ type: 'MARK_ALL_NOTIFICATIONS_READ' });
  }, []);

  const dismissNotification = useCallback((notificationId: string) => {
    dispatch({ type: 'REMOVE_NOTIFICATION', payload: notificationId });
  }, []);

  // Utility functions
  const getRoomTabs = useCallback((): RoomTab[] => {
    return Array.from(state.activeRooms.values()).map(room => ({
      roomId: room.roomId,
      gameType: room.gameType,
      playerCount: room.playerCount,
      maxPlayers: room.maxPlayers,
      status: room.status,
      isActive: room.isActive,
      hasNotification: state.notifications.some(n => 
        n.roomId === room.roomId && !n.isRead && room.roomId !== state.activeRoomId
      ),
      joinedAt: room.joinedAt,
    }));
  }, [state.activeRooms, state.notifications, state.activeRoomId]);

  const getActiveRoom = useCallback((): ActiveRoom | null => {
    return state.activeRoomId ? state.activeRooms.get(state.activeRoomId) || null : null;
  }, [state.activeRoomId, state.activeRooms]);

  const isInRoom = useCallback((roomId: string): boolean => {
    return state.activeRooms.has(roomId);
  }, [state.activeRooms]);

  // API data fetching
  const fetchUserRooms = useCallback(async () => {
    try {
      // Using apiClient (handles 401 auto-refresh)
      const response = await apiClient.get('/user/rooms');
      const rooms = response.data.data.activeRooms;
      
      // Clear existing rooms and add fetched ones
      const newActiveRooms = new Map<string, ActiveRoom>();
      rooms.forEach((room: any) => {
        newActiveRooms.set(room.roomId, {
          roomId: room.roomId,
          gameType: room.gameType,
          playerCount: 0, // Will be updated from game state
          maxPlayers: 4,
          status: 'waiting',
          joinedAt: new Date(room.joinedAt),
          isActive: room.isActive,
          isConnected: false, // Will be updated when WebSocket connects
          lastActivity: new Date()
        });
      });
      
      // Update state
      // This would need a custom action type, for now just add rooms individually
      newActiveRooms.forEach(room => {
        dispatch({ type: 'ADD_ROOM', payload: room });
      });

    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to fetch rooms' });
    }
  }, []);

  const fetchRoomCapacity = useCallback(async () => {
    try {
      // Using apiClient (handles 401 auto-refresh)
      const response = await apiClient.get('/user/rooms/capacity');
      const data = response.data.data;
      
      dispatch({ 
        type: 'UPDATE_ROOM_CAPACITY', 
        payload: { 
          current: data.currentActiveRooms, 
          max: data.maxActiveRooms 
        } 
      });
      dispatch({ type: 'SET_MAX_ROOMS', payload: data.maxActiveRooms });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to fetch capacity' });
    }
  }, []);

  // Load initial data and set up WebSocket handlers
  useEffect(() => {
    // Set up WebSocket event handlers
    multiRoomWebSocket.setEventHandlers({
      onRoomUpdate: (roomId: string, state: any) => {
        console.log(`Room ${roomId} state updated:`, state);
        dispatch({ 
          type: 'UPDATE_ROOM', 
          payload: { 
            roomId, 
            updates: { 
              gameState: state,
              lastActivity: new Date(),
              playerCount: state.players?.length || 0,
              status: state.phase === 'playing' ? 'playing' : 'waiting'
            } 
          } 
        });
      },
      onRoomError: (roomId: string, error: string) => {
        console.error(`Room ${roomId} error:`, error);
        addNotification({
          roomId,
          type: 'message',
          title: 'Connection Error',
          message: `Error in room ${roomId.slice(-4)}: ${error}`,
          isRead: false,
          priority: 'high',
        });
      },
      onPlayerAction: (roomId: string, action: string, data: any) => {
        console.log(`Room ${roomId} player action:`, action, data);
        // Handle different action types
        switch (action) {
          case 'player_joined':
            addNotification({
              roomId,
              type: 'player-joined',
              title: 'Player Joined',
              message: `${data.username} joined the room`,
              isRead: false,
              priority: 'low',
            });
            break;
          case 'your_turn':
            addNotification({
              roomId,
              type: 'your-turn',
              title: 'Your Turn!',
              message: 'It\'s your turn to play',
              isRead: false,
              priority: 'urgent',
            });
            break;
          case 'game_started':
            addNotification({
              roomId,
              type: 'game-started',
              title: 'Game Started',
              message: 'The game has begun!',
              isRead: false,
              priority: 'high',
            });
            break;
        }
      },
      onConnectionChange: (roomId: string, isConnected: boolean) => {
        console.log(`Room ${roomId} connection changed:`, isConnected);
        dispatch({ 
          type: 'UPDATE_ROOM', 
          payload: { 
            roomId, 
            updates: { 
              isConnected,
              lastActivity: new Date()
            } 
          } 
        });
      }
    });

    // Start cleanup timer
    multiRoomWebSocket.startCleanupTimer();

    fetchUserRooms();
    fetchRoomCapacity();

    // Cleanup function
    return () => {
      multiRoomWebSocket.disconnectAll();
    };
  }, [fetchUserRooms, fetchRoomCapacity]);

  const value: MultiRoomContextType = {
    state,
    joinRoom,
    leaveRoom,
    switchRoom,
    updateRoomStatus,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    dismissNotification,
    getRoomTabs,
    getActiveRoom,
    isInRoom,
    fetchUserRooms,
    fetchRoomCapacity,
  };

  return (
    <MultiRoomContext.Provider value={value}>
      {children}
    </MultiRoomContext.Provider>
  );
};

// Custom hook
export const useMultiRoom = (): MultiRoomContextType => {
  const context = useContext(MultiRoomContext);
  if (!context) {
    throw new Error('useMultiRoom must be used within a MultiRoomProvider');
  }
  return context;
};